import React, { useEffect, useMemo, useState } from "react";

import { Col, Container, Row } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";

import type { OperationVariables } from "@apollo/client";

import {
  FilterBy,
  ListPaymentLinesQuery,
  ListPaymentLinesQueryVariables,
  type PaymentLineDetailsWithResaleDetails,
  type ResaleFragment,
  UserStatus,
} from "@api/graphql";
import { PendingNotification } from "@components/PendingNotification";
import { UserDisplay } from "@components/UserDisplay";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import DateFilter from "@components/table/DateFilter";
import SearchInput from "@components/table/SearchInput";
import { useAuthenticationContext } from "@context/authentication";
import { DetailsDrawer } from "@pages/dashboard/DetailsDrawer";
import { type PaginatedOutputGeneric, getPageHeading } from "@util/index";
import type { QueryFunction, QueryOptions } from "@util/query";

import { HorizontalButtonPager } from "../table/Pager";
import { RoyaltiesTable } from "../table/RoyaltiesTable";
import type { TableHeader } from "../table/TableContents";

export type RoyaltiesSourcePageType = "UpcomingRoyalties" | "PastPayments";

interface RoyaltiesProps<TData, TVariables extends OperationVariables> {
  queryFunction: QueryFunction<TData, TVariables>;
  getQueryVariables: (
    page: number,
    filters?: { searchText: string; dateRange: { startDate: string | null; endDate: string | null } },
  ) => TVariables;
  updateQueryVariablesWithFilter: (
    currentQueryVariables: TVariables,
    new_filter_by: FilterBy[] | undefined,
  ) => TVariables;
  getPaymentLinesFromQueryData: (data: TData) => PaymentLineDetailsWithResaleDetails[];
  getPaginatedOutputFromQueryData: (data: TData) => PaginatedOutputGeneric<PaymentLineDetailsWithResaleDetails>;
  customTableHeaders?: TableHeader<PaymentLineDetailsWithResaleDetails>[];
  sourcePage?: RoyaltiesSourcePageType;
  customTableStyleClassName?: string;
  displayBanner?: boolean;
}

export const Royalties = ({
  queryFunction,
  getQueryVariables,
  updateQueryVariablesWithFilter,
  getPaymentLinesFromQueryData,
  getPaginatedOutputFromQueryData,
  customTableHeaders,
  sourcePage,
  customTableStyleClassName,
  displayBanner = true,
}: RoyaltiesProps<ListPaymentLinesQuery, ListPaymentLinesQueryVariables>) => {
  const { auth } = useAuthenticationContext();

  const [page, setPage] = useState(1);
  const [paymentLines, setPaymentLines] = useState<PaymentLineDetailsWithResaleDetails[]>([]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentSelectedRowResaleData, setCurrentSelectedRowResaleData] = useState<ResaleFragment>();

  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState<{ startDate: string | null; endDate: string | null }>({
    startDate: null,
    endDate: null,
  });

  const pageHeading = getPageHeading(sourcePage, "Royalties");
  const isNumeric = (value: string) => !Number.isNaN(Number(value)) && Number.isFinite(Number(value));

  const filters = useMemo(() => {
    const filterArray: FilterBy[] = [];
    if (searchText) {
      if (isNumeric(searchText)) {
        filterArray.push({ field: "resale_id", eq: searchText });
      } else {
        filterArray.push({ field: "search_name", iLike: `%${searchText}%` });
      }
    }
    if (dateRange.startDate) {
      filterArray.push({ field: "date_created", gte: dateRange.startDate });
    }
    if (dateRange.endDate) {
      filterArray.push({ field: "date_created", lte: dateRange.endDate });
    }
    return filterArray;
  }, [searchText, dateRange]);

  const queryVariables = useMemo(() => {
    const baseVariables = getQueryVariables(page);
    return updateQueryVariablesWithFilter(baseVariables, filters);
  }, [getQueryVariables, page, updateQueryVariablesWithFilter, filters]);

  const queryOptions: QueryOptions<ListPaymentLinesQuery, ListPaymentLinesQueryVariables> = useMemo(
    () => ({
      variables: queryVariables,
      fetchPolicy: "no-cache",
      errorPolicy: "none",
    }),
    [queryVariables],
  );

  const { data, loading, refetch } = queryFunction(queryOptions);

  const paginatedOutput = data ? getPaginatedOutputFromQueryData(data) : undefined;

  useEffect(() => {
    if (data) {
      const paymentLines = getPaymentLinesFromQueryData(data);
      setPaymentLines(paymentLines);
    }
  }, [data, getPaymentLinesFromQueryData]);

  const handleOnTableRowClick = (rowData: PaymentLineDetailsWithResaleDetails) => {
    setCurrentSelectedRowResaleData(rowData.resale);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    if (!currentSelectedRowResaleData) {
      return;
    }

    const currentRowResaleId = currentSelectedRowResaleData.resale_id;

    const currentRowToUpdate = paymentLines.find(({ resale }) => resale?.resale_id === currentRowResaleId);

    setCurrentSelectedRowResaleData(currentRowToUpdate?.resale);

    // this useEffect is used for updating the row data when the API refetch,
    // so not putting currentSelectedRowData in the dependencies as row click handler will handle that case
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentLines]);

  const enterPressed = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchText(e.currentTarget.value);
    }
  };

  useEffect(() => {
    setPage(1);
    refetch();
  }, [filters, refetch]);

  return (
    <>
      {currentSelectedRowResaleData && (
        <DetailsDrawer
          resale={currentSelectedRowResaleData}
          isDrawerOpen={isDrawerOpen}
          refetch={refetch}
          onHide={handleDrawerClose}
        />
      )}

      <Container className="p-4" fluid>
        {displayBanner && (
          <>
            <Row className="pb-4">
              <Col>
                <UserDisplay />
              </Col>
            </Row>
            <ProtectedComponent requiredUserStatus={UserStatus.PENDING_APPROVAL} auth={auth}>
              <Row key={"invoice"}>
                <PendingNotification title={<span>We are still reviewing your account.</span>} />
              </Row>
            </ProtectedComponent>
            <Row>
              <Col>
                <h3>{pageHeading.heading}</h3>
                <span className="b3">{pageHeading.subheading}</span>
              </Col>
            </Row>
          </>
        )}

        <Row className="mt-3">
          <Col md={3}>
            <SearchInput searchText={searchText} setSearchText={setSearchText} enterPressed={enterPressed} />
          </Col>
          <Col md={3}>
            <DateFilter setDateRange={setDateRange} />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <Row>
              <Col>
                {loading && (
                  <div className="w-100 d-flex justify-content-center">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                )}
                {!loading && (
                  <div className="p-2 pt-0 d-flex justify-content-start align-items-start align-self-stretch flex-column">
                    <RoyaltiesTable
                      onRowClick={handleOnTableRowClick}
                      paymentLines={paymentLines}
                      customTableHeaders={customTableHeaders}
                      customTableStyleClassName={customTableStyleClassName}
                    />
                  </div>
                )}
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <HorizontalButtonPager
                  page={page}
                  totalPages={paginatedOutput?.total_pages || 0}
                  totalItems={paginatedOutput?.total_items || 0}
                  setPage={setPage}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};
